import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtUSD, shortAddr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Shield, LogOut, Loader2, QrCode } from "lucide-react";

export default function AdminConsole() {
  const { user, isLoading, isAdmin, logout, setUser } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAdmin) nav("/admin");
  }, [isLoading, isAdmin, nav]);

  if (isLoading) return <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 text-stone-500">authenticating…</div>;
  if (!isAdmin) return null;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FFC940]">
            <Shield className="w-4 h-4" /> admin · {user.email}
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black">campfire controls</h1>
        </div>
        <Button variant="outline" onClick={async () => { await logout(); nav("/admin"); }} className="border-[#3A2E1C] bg-transparent hover:bg-[#141008]" data-testid="admin-logout">
          <LogOut className="w-4 h-4 mr-2" /> logout
        </Button>
      </div>

      <Tabs defaultValue="snapshots" className="mt-6">
        <TabsList className="bg-[#141008] border border-[#3A2E1C]">
          <TabsTrigger value="snapshots" data-testid="tab-snapshots">Snapshots</TabsTrigger>
          <TabsTrigger value="treasury" data-testid="tab-treasury">Treasury</TabsTrigger>
          <TabsTrigger value="quotrons" data-testid="tab-quotrons">Quotrons</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Yield Payouts</TabsTrigger>
          <TabsTrigger value="moderate" data-testid="tab-moderate">Moderation</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">2FA</TabsTrigger>
        </TabsList>

        <TabsContent value="snapshots"><SnapshotsPanel /></TabsContent>
        <TabsContent value="treasury"><TreasuryPanel /></TabsContent>
        <TabsContent value="quotrons"><QuotronsPanel /></TabsContent>
        <TabsContent value="payouts"><PayoutsPanel /></TabsContent>
        <TabsContent value="moderate"><ModeratePanel /></TabsContent>
        <TabsContent value="security"><TwoFactorPanel user={user} setUser={setUser} /></TabsContent>
      </Tabs>
    </div>
  );
}

function SnapshotsPanel() {
  const now = new Date();
  const defaultPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [period, setPeriod] = useState(defaultPeriod);
  const [total, setTotal] = useState("1000000");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  const load = async () => { try { const r = await api.get("/admin/snapshots"); setList(r.data || []); } catch {} };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/snapshots", { period_label: period, total_quodust_to_airdrop: parseFloat(total), note });
      toast.success(`snapshot · ${r.data.wallet_count} wallets · ${fmtUSD(r.data.period_total_usd)} total donated`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "snapshot failed"); }
    finally { setLoading(false); }
  };

  const exportCsv = (snap) => {
    const header = "wallet,usd_donated,share,quodust_allocated\n";
    const rows = (snap.allocations || []).map(a => `${a.wallet},${a.usd_donated},${a.share},${a.quodust_allocated}`).join("\n");
    const url = URL.createObjectURL(new Blob([header + rows], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `snapshot-${snap.period_label}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
      <div className="grid sm:grid-cols-4 gap-3">
        <div><Label>Period</Label><Input data-testid="snap-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div><Label>$QUODUST to airdrop</Label><Input data-testid="snap-total" value={total} onChange={(e) => setTotal(e.target.value)} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div className="sm:col-span-2"><Label>Note</Label><Input data-testid="snap-note" value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
      </div>
      <Button onClick={run} disabled={loading} data-testid="snap-run" className="mt-4 bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} run snapshot
      </Button>

      <div className="mt-6 space-y-3">
        {list.map((s) => (
          <div key={s.id} className="rounded-md border border-[#3A2E1C] bg-[#0A0906] p-4" data-testid="snap-row">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="font-display text-lg">{s.period_label}</div>
                <div className="text-xs text-stone-500">{s.wallet_count} wallets · {fmtUSD(s.period_total_usd)} donated · {Number(s.total_quodust_to_airdrop).toLocaleString()} QDT</div>
              </div>
              <Button onClick={() => exportCsv(s)} variant="outline" className="border-[#3A2E1C] bg-transparent hover:bg-[#1E1810]" data-testid={`export-${s.period_label}`}>export CSV</Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-sm text-stone-500">no snapshots yet</div>}
      </div>
    </div>
  );
}

function TreasuryPanel() {
  const [form, setForm] = useState({
    quotrons_tier1: 0, quotrons_tier2: 0, quotrons_tier3: 0,
    nvda_yield_usd: 0, tsla_yield_usd: 0, aapl_yield_usd: 0, spy_yield_usd: 0,
    quodust_distributed_usd: 0, note: "",
  });
  useEffect(() => {
    api.get("/treasury").then((r) => {
      const d = r.data; setForm({
        quotrons_tier1: d.quotrons_tier1 || 0, quotrons_tier2: d.quotrons_tier2 || 0, quotrons_tier3: d.quotrons_tier3 || 0,
        nvda_yield_usd: d.nvda_yield_usd || 0, tsla_yield_usd: d.tsla_yield_usd || 0,
        aapl_yield_usd: d.aapl_yield_usd || 0, spy_yield_usd: d.spy_yield_usd || 0,
        quodust_distributed_usd: d.quodust_distributed_usd || 0, note: d.note || "",
      });
    });
  }, []);
  const num = (k) => (e) => setForm({ ...form, [k]: parseFloat(e.target.value || 0) });
  const save = async () => {
    try { await api.put("/treasury", form); toast.success("treasury updated"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "save failed"); }
  };
  return (
    <div className="mt-4 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5" data-testid="treasury-panel">
      <div className="grid sm:grid-cols-4 gap-3">
        {["quotrons_tier1","quotrons_tier2","quotrons_tier3"].map(k => (
          <div key={k}><Label>{k.replace(/_/g," ")}</Label><Input type="number" value={form[k]} onChange={num(k)} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        ))}
        {["nvda_yield_usd","tsla_yield_usd","aapl_yield_usd","spy_yield_usd","quodust_distributed_usd"].map(k => (
          <div key={k}><Label>{k.replace(/_/g," ")}</Label><Input type="number" step="any" value={form[k]} onChange={num(k)} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        ))}
        <div className="sm:col-span-3"><Label>Note</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
      </div>
      <Button onClick={save} data-testid="treasury-save" className="mt-4 bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold">save</Button>
    </div>
  );
}

function QuotronsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ token_id: "", tier: 1, image_url: "", opensea_url: "", note: "" });
  const [loading, setLoading] = useState(false);
  const load = async () => {
    try { const r = await api.get("/quotrons"); setItems(r.data?.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.token_id) { toast.error("token id required"); return; }
    setLoading(true);
    try {
      await api.post("/quotrons", {
        token_id: parseInt(form.token_id, 10),
        tier: parseInt(form.tier, 10) || 1,
        image_url: form.image_url || null,
        opensea_url: form.opensea_url || null,
        note: form.note || null,
      });
      toast.success(`QUOTRON #${form.token_id} hardwired`);
      setForm({ token_id: "", tier: 1, image_url: "", opensea_url: "", note: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "add failed"); }
    finally { setLoading(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/quotrons/${id}`); toast.success("removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "delete failed"); }
  };
  return (
    <div className="mt-4 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5" data-testid="quotrons-admin-panel">
      <div className="text-xs text-stone-500 mb-3 mono">Robinhood Chain · contract 0x5a86…40D7F · image + opensea auto-filled from token id</div>
      <div className="grid sm:grid-cols-6 gap-3">
        <div><Label>Token ID</Label><Input data-testid="quotron-token-id" value={form.token_id} onChange={(e) => setForm({ ...form, token_id: e.target.value })} placeholder="42" className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div>
          <Label>Tier</Label>
          <Input data-testid="quotron-tier" type="number" min="1" max="5" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" />
        </div>
        <div className="sm:col-span-2"><Label>Image URL (optional)</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://… or ipfs://" className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div className="sm:col-span-2"><Label>OpenSea URL (optional)</Label><Input value={form.opensea_url} onChange={(e) => setForm({ ...form, opensea_url: e.target.value })} placeholder="https://opensea.io/item/…" className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div className="sm:col-span-6"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
      </div>
      <Button onClick={add} disabled={loading} data-testid="quotron-add" className="mt-4 bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} hardwire
      </Button>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((q) => (
          <div key={q.id} className="rounded-md border border-[#3A2E1C] bg-[#0A0906] p-3 flex items-center gap-3" data-testid={`quotron-admin-row-${q.token_id}`}>
            <img src={q.image_url} alt="" className="w-14 h-14 rounded-md object-cover border border-[#3A2E1C] bg-[#141008]" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg leading-none">#{q.token_id}</div>
              <div className="text-xs text-stone-500 mt-0.5 mono">T{q.tier} · {q.tier_name}</div>
              <a href={q.opensea_url} target="_blank" rel="noreferrer" className="text-xs text-[#FFC940] underline underline-offset-2">opensea ↗</a>
            </div>
            <Button variant="outline" className="border-[#3A2E1C] bg-transparent text-xs h-8 px-2" onClick={() => remove(q.id)} data-testid={`quotron-del-${q.token_id}`}>remove</Button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-stone-500 col-span-full">no quotrons yet</div>}
      </div>
    </div>
  );
}

function PayoutsPanel() {
  const [form, setForm] = useState({ period_label: "", total_pool_usd: "", nvda: 0, tsla: 0, aapl: 0, spy: 0, note: "" });
  const [rows, setRows] = useState([]);
  const load = async () => { const r = await api.get("/holders/payouts"); setRows(r.data || []); };
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      await api.post("/holders/payouts", {
        period_label: form.period_label,
        total_pool_usd: parseFloat(form.total_pool_usd || 0),
        stock_breakdown: { NVDA: +form.nvda || 0, TSLA: +form.tsla || 0, AAPL: +form.aapl || 0, SPY: +form.spy || 0 },
        note: form.note,
      });
      toast.success("payout logged"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "failed"); }
  };
  return (
    <div className="mt-4 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
      <div className="grid sm:grid-cols-6 gap-3">
        <div><Label>Period</Label><Input value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} placeholder="2026-02" className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div><Label>Pool $</Label><Input value={form.total_pool_usd} onChange={(e) => setForm({ ...form, total_pool_usd: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div><Label>NVDA</Label><Input value={form.nvda} onChange={(e) => setForm({ ...form, nvda: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div><Label>TSLA</Label><Input value={form.tsla} onChange={(e) => setForm({ ...form, tsla: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div><Label>AAPL</Label><Input value={form.aapl} onChange={(e) => setForm({ ...form, aapl: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
        <div><Label>SPY</Label><Input value={form.spy} onChange={(e) => setForm({ ...form, spy: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
      </div>
      <div className="mt-3"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-[#0A0906] border-[#3A2E1C]" /></div>
      <Button onClick={submit} data-testid="payout-save" className="mt-4 bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold">log payout</Button>
      <div className="mt-6 space-y-2">
        {rows.map((p, i) => (
          <div key={i} className="rounded-md border border-[#3A2E1C] bg-[#0A0906] p-3 text-sm flex justify-between">
            <span className="mono">{p.period_label}</span><span className="text-[#FFD54F]">{fmtUSD(p.total_pool_usd)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModeratePanel() {
  const [posts, setPosts] = useState([]);
  const load = async () => { const r = await api.get("/community/posts?limit=200&include_hidden=true"); setPosts(r.data || []); };
  useEffect(() => { load(); }, []);
  const toggle = async (id, hidden) => {
    try { await api.post(`/admin/community/posts/${id}/moderate`, { hidden }); load(); toast.success(hidden ? "hidden" : "visible"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "failed"); }
  };
  return (
    <div className="mt-4 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5 space-y-2">
      {posts.map((p) => (
        <div key={p.id} className="rounded-md border border-[#3A2E1C] bg-[#0A0906] p-3 text-sm flex items-start justify-between gap-3" data-testid="moderate-row">
          <div>
            <div className="text-xs text-stone-500">{shortAddr(p.wallet)} · {new Date(p.created_at).toLocaleString()}</div>
            <div className={`mt-1 ${p.hidden ? "text-stone-500 line-through" : "text-stone-200"}`}>{p.content}</div>
          </div>
          <Button variant="outline" className="border-[#3A2E1C] bg-transparent" onClick={() => toggle(p.id, !p.hidden)} data-testid={`moderate-${p.id}`}>
            {p.hidden ? "unhide" : "hide"}
          </Button>
        </div>
      ))}
    </div>
  );
}

function TwoFactorPanel({ user, setUser }) {
  const [setup, setSetup] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const begin = async () => {
    setLoading(true);
    try { const r = await api.post("/auth/2fa/setup"); setSetup(r.data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "failed"); }
    finally { setLoading(false); }
  };
  const enable = async () => {
    try {
      await api.post("/auth/2fa/enable", { otp });
      toast.success("2FA enabled");
      const me = await api.get("/auth/me"); setUser(me.data);
      setSetup(null); setOtp("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "invalid code"); }
  };
  const disable = async () => {
    try {
      await api.post("/auth/2fa/disable", { otp });
      toast.success("2FA disabled");
      const me = await api.get("/auth/me"); setUser(me.data);
      setOtp("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "invalid code"); }
  };

  return (
    <div className="mt-4 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5" data-testid="security-panel">
      <div className="text-sm">
        status: {user?.totp_enabled ? <span className="text-[#10B981]">enabled</span> : <span className="text-stone-400">disabled</span>}
      </div>
      {!user?.totp_enabled && (
        <>
          <Button onClick={begin} disabled={loading} className="mt-4 bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold" data-testid="2fa-begin">
            <QrCode className="w-4 h-4 mr-2" /> generate TOTP
          </Button>
          {setup && (
            <div className="mt-5 rounded-md border border-[#3A2E1C] bg-[#0A0906] p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <img alt="qr" src={`data:image/png;base64,${setup.qr_png_base64}`} className="w-40 h-40 rounded-md border border-[#3A2E1C] bg-white p-1" />
                <div className="flex-1">
                  <div className="text-xs text-stone-500 mb-1">scan with authenticator, or paste secret:</div>
                  <div className="mono text-sm break-all text-[#FFD54F]">{setup.secret}</div>
                  <div className="mt-4">
                    <Label>enter 6-digit code</Label>
                    <Input value={otp} onChange={(e) => setOtp(e.target.value)} className="bg-[#0A0906] border-[#3A2E1C] mono" data-testid="2fa-otp" />
                  </div>
                  <Button onClick={enable} className="mt-3 bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold" data-testid="2fa-enable">enable</Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {user?.totp_enabled && (
        <div className="mt-4 flex gap-2 items-end">
          <div><Label>TOTP code</Label><Input value={otp} onChange={(e) => setOtp(e.target.value)} className="bg-[#0A0906] border-[#3A2E1C] mono" data-testid="2fa-otp-disable" /></div>
          <Button onClick={disable} variant="outline" className="border-[#3A2E1C] bg-transparent" data-testid="2fa-disable">disable</Button>
        </div>
      )}
    </div>
  );
}
